/* eslint-disable react/forbid-prop-types,react/destructuring-assignment */
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState, useRef, useLayoutEffect } from 'react';
import { Rnd } from 'react-rnd';

const InnerEvents = ['onMouseOver', 'onMouseEnter', 'onMouseMove', 'onMouseLeave', 'onClick'];

const mapInnerEvents = (fn) => _.mapValues(_.keyBy(InnerEvents), fn);

const adjusted = (offsets, orgPos, dims, reverse) => {
	const ofs = typeof offsets === 'function' ? offsets(dims) : offsets;
	const { x: ofsX = 0, y: ofsY = 0 } = ofs || {};
	const mult = reverse ? -1 : 1;
	return { x: orgPos.x + (ofsX * mult), y: orgPos.y + (ofsY * mult) };
};

const elmDims = (elm) => _.pick(elm.getBoundingClientRect(), ['width', 'height']);

export function MovableBox(props) {
	const {
		maxInitHeight,
		pixelCoords: [x, y],
		children,
		onNewCoords,
		offsets,
	} = props;
	const rest = _.omit(props, _.keys(MovableBox.propTypes));
	const ref = useRef(null);
	const [pos, setPos] = useState(null);
	const [minSize, setMinSize] = useState(null);
	const [lastSize, setLastSize] = useState(null);
	const innerEvents = mapInnerEvents((cbName) => props[cbName]);

	// We need to figure out the size of the content here. Mainly as the initial positition should be one "height" above
	// y and half "width" to the left of x. We also need to set "minSize" as it shouldn't be possible to shrink the
	// box (as it looks terrible).
	// For this purpose, we'll create a temporary div that will be added to DOM with the children - that we'll measure.
	// Credit: AI
	useLayoutEffect(() => {
		let dims = lastSize || minSize;
		if (!minSize) { // This implies this is the callback we're getting after the test-div as been added to DOM
			const refDims = elmDims(ref.current);
			if (maxInitHeight) {
				refDims.height = Math.min(refDims.height, maxInitHeight);
			}
			dims = refDims;
			setMinSize(refDims);
		}
		setPos(adjusted(offsets, { x, y }, dims));
	}, [x, y, minSize, lastSize, offsets, maxInitHeight]);

	if (!pos) {
		return (
			<div
				ref={ref}
				style={{ position: 'absolute', visibility: 'hidden', left: x, top: y }}
			>
				{children}
			</div>
		);
	}

	const updateCoords = (params) => {
		const dims = elmDims(params.node);
		setLastSize(dims);
		const newCoords = adjusted(offsets, params, dims, true);
		onNewCoords?.([newCoords.x, newCoords.y]);
	};

	const onDragStop = (__, params) => updateCoords(params);
	const onResizeStop = (e, dir, node, delta, position) => updateCoords({ ...position, node });

	return (
		<Rnd
			position={pos}
			default={{ x: pos.x, y: pos.y, width: minSize.width, height: minSize.height }}
			minWidth={minSize.width}
			minHeight={minSize.height}
			onDragStop={onDragStop}
			onResizeStop={onResizeStop}
			{...rest}
		>
			<div
				style={{ width: '100%', height: '100%' }}
				{...innerEvents}
			>
				{children}
			</div>
		</Rnd>
	);
}

MovableBox.propTypes = {
	maxInitHeight: PropTypes.number,
	pixelCoords: PropTypes.arrayOf(PropTypes.number).isRequired,
	children: PropTypes.any,
	onNewCoords: PropTypes.func,
	offsets: PropTypes.any,
	...mapInnerEvents(() => PropTypes.func),
};

MovableBox.defaultProps = {
	maxInitHeight: undefined,
	children: undefined,
	onNewCoords: undefined,
	offsets: undefined,
	...mapInnerEvents(() => undefined),
};
